/**
 * Local building blocks of the learner space (design v2): activity rings per skill (Apple
 * Fitness-like), stat tiles, section titles, skill icons and the page head. Tokens only.
 * The rings and the skill tile now come from @agencyos/ui-web (v2.1); this file maps the skills.
 */

import {
  ActivityRings as SharedActivityRings,
  Ring as SharedRing,
  SkillIcon as SharedSkillIcon,
  type Tone,
} from "@agencyos/ui-web";
import { BookOpenText, Headphones, Mic, PenLine } from "lucide-react";
import type { ReactNode } from "react";

import type { Skill } from "./types";

export const SKILL_ICON: Record<Skill, typeof Headphones> = {
  co: Headphones,
  ce: BookOpenText,
  ee: PenLine,
  eo: Mic,
};

/** Skill → tone of the shared rings and tiles (@agencyos/ui-web): CO accent, CE green, EE amber, EO red. */
export const SKILL_TONE: Record<Skill, Tone> = { co: "accent", ce: "success", ee: "warning", eo: "red" };
type LocalTone = Skill | "accent" | "success" | "warning" | "danger";
const toneOf = (tone: LocalTone): Tone => (tone in SKILL_TONE ? SKILL_TONE[tone as Skill] : (tone as Tone));

/** Coloured pastille with the icon of a skill (identity is never colour alone: icon + name).
 * Now the shared SkillIcon tile of @agencyos/ui-web; the ac-* classes keep the local sizes. */
export function SkillIcon({ skill, size = 18, className = "" }: { skill: Skill; size?: number; className?: string }) {
  const Icon = SKILL_ICON[skill];
  return (
    <SharedSkillIcon
      icon={<Icon size={size} strokeWidth={1.9} />}
      tone={SKILL_TONE[skill]}
      className={`ac-pastille ac-skill--${skill} ${className}`.trim()}
    />
  );
}

export interface RingValue {
  skill: Skill;
  /** 0 … 1 (1 = the goal is reached). */
  ratio: number;
}

/** Concentric activity rings, one per skill, outermost first (shared ActivityRings). */
export function ActivityRings({
  rings,
  size = 200,
  stroke = 16,
  label,
  children,
}: {
  rings: RingValue[];
  size?: number;
  stroke?: number;
  label?: string;
  children?: ReactNode;
}) {
  return (
    <SharedActivityRings
      className="ac-rings"
      rings={rings.map((ring) => ({ key: ring.skill, tone: SKILL_TONE[ring.skill], ratio: ring.ratio }))}
      size={size}
      stroke={stroke}
      label={label}
    >
      {children}
    </SharedActivityRings>
  );
}

/** One ring with a value in the middle (shared Ring). */
export function Ring({
  ratio,
  size = 120,
  stroke = 10,
  tone = "accent",
  children,
  className = "",
}: {
  ratio: number;
  size?: number;
  stroke?: number;
  tone?: LocalTone;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <SharedRing ratio={ratio} size={size} stroke={stroke} tone={toneOf(tone)} className={`ac-ring-one ac-tone--${tone} ${className}`.trim()}>
      {children}
    </SharedRing>
  );
}

/** Title of a group of cards (h2), with an optional action on the right. */
export function SectionTitle({ id, children, action }: { id?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="ac-section-title">
      <h2 id={id}>{children}</h2>
      {action}
    </div>
  );
}

/** Large page head: eyebrow, the single h1, a subtitle and actions. */
export function PageHead({
  eyebrow,
  title,
  subtitle,
  aside,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <header className="ac-head">
      <div className="ac-head__text">
        {eyebrow ? <p className="ac-eyebrow">{eyebrow}</p> : null}
        <h1 className="ac-head__title">{title}</h1>
        {subtitle ? <div className="ac-head__subtitle">{subtitle}</div> : null}
      </div>
      {aside ? <div className="ac-head__aside">{aside}</div> : null}
    </header>
  );
}

/** Card title with its icon pastille (h2 inside a card). */
export function CardTitle({
  id,
  icon,
  tone = "accent",
  children,
  as: Tag = "h2",
}: {
  id?: string;
  icon: ReactNode;
  tone?: Skill | "accent" | "success" | "warning" | "danger" | "info";
  children: ReactNode;
  as?: "h1" | "h2" | "h3";
}) {
  return (
    <Tag id={id} className="ac-card-title">
      <span className={`ac-pastille ac-tone--${tone}`} aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </Tag>
  );
}
